import React, { useState, useEffect, useMemo } from 'react'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { DataTable, DataTableSortEvent, DataTableRowClickEvent } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { BreadCrumb } from 'primereact/breadcrumb'
import { MenuItem } from 'primereact/menuitem'
import { formatDateTime } from '@/utils/format'
import { useAtomValue } from 'jotai'
import { toastAtom } from '@/atoms/toastAtom'
import { FilesService, OneDriveFile } from '@/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

interface OneDriveFileSelectorModalProps {
    visible: boolean
    onClose: () => void
}

function OneDriveFileSelectorModal({ visible, onClose }: OneDriveFileSelectorModalProps) {
  // 認証情報
  const toast = useAtomValue(toastAtom)
  const queryClient = useQueryClient()

  // 状態管理
  const [currentPath, setCurrentPath] = useState<string | null>(null)
  const [filterText, setFilterText] = useState<string>('')
  const [selectedFile, setSelectedFile] = useState<OneDriveFile | null>(null)
  const [breadcrumbItems, setBreadcrumbItems] = useState<MenuItem[]>([])
  const [pathHistory, setPathHistory] = useState<{ name: string; path: string | null }[]>([])
  const [sortField, setSortField] = useState<string>('type')
  const [sortOrder, setSortOrder] = useState<1 | -1>(1) // 1: 昇順, -1: 降順

  // OneDriveファイル一覧を取得
  const { data: oneDriveData, isLoading } = useQuery({
    queryKey: ['onedriveFiles', currentPath],
    queryFn: () => FilesService.listOnedriveFiles(currentPath),
    enabled: visible,
    refetchOnWindowFocus: false
  })

  //ファイル選択Mutation
  const fileSelectMutation = useMutation({
    mutationFn: (fileId: string) => FilesService.downloadOnedriveFile({
      file_id: fileId
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['selectedFile'] })
      toast?.show({
        severity: 'success',
        summary: 'ファイル選択',
        detail: `${selectedFile?.name} が選択されました`,
        life: 3000
      })
      onClose()
    },
    onError: () => {
      toast?.show({
        severity: 'error',
        summary: 'エラー',
        detail: 'ファイルの選択に失敗しました',
        life: 3000
      })
    }
  })

  // フィルタリングされたファイル一覧
  const filteredFiles = useMemo(() => {
    if (!oneDriveData || !oneDriveData.files) return []
    const filtered = oneDriveData.files
      .filter(file =>
        (file.type === 'folder' || file.is_excel) &&
        file.name.toLowerCase().includes(filterText.toLowerCase())
      )
      .map(file => ({
        ...file,
        fileType: file.type === 'folder' ? 'フォルダ' : 'Excelファイル',
        lastModifiedTimestamp: new Date(file.last_modified).getTime()
      }))

    return [...filtered].sort((a, b) => {
      if (sortField === 'type' || sortField === 'fileType') {
        if (a.type === b.type) {
          return a.name.localeCompare(b.name) * sortOrder
        }
        return a.type === 'folder' ? -1 * sortOrder : 1 * sortOrder
      } else if (sortField === 'name') {
        return a.name.localeCompare(b.name) * sortOrder
      } else if (sortField === 'size') {
        return (a.raw_size - b.raw_size) * sortOrder
      } else if (sortField === 'last_modified') {
        const dateA = new Date(a.last_modified).getTime()
        const dateB = new Date(b.last_modified).getTime()
        return (dateA - dateB) * sortOrder
      }
      return 0
    })
  }, [oneDriveData, filterText, sortField, sortOrder])

  // パンくずリストの更新
  useEffect(() => {
    const items = pathHistory.map((item, index) => ({
      label: item.name,
      command: () => {
        setCurrentPath(item.path)
        setPathHistory(pathHistory.slice(0, index + 1))
        setSelectedFile(null)
      }
    }))

    setBreadcrumbItems(items.slice())
  }, [pathHistory])

  // フォルダクリック時の処理
  const handleFolderClick = (folder: OneDriveFile) => {
    const nextPath = currentPath ? `${currentPath}/${folder.name}` : folder.name
    setCurrentPath(nextPath)
    setPathHistory((prev) => [...prev, { name: folder.name, path: nextPath }])
    setSelectedFile(null)
  }

  // 行クリック処理
  const handleRowClick = (e: DataTableRowClickEvent) => {
    const item = e.data as OneDriveFile
    if (item.type === 'folder') {
      handleFolderClick(item)
    } else if (item.is_excel) {
      setSelectedFile(item)
    }
  }

  // ファイル選択時の処理
  const handleProceed = () => {
    if (selectedFile) {
      // TODO: API仕様変更に合わせ、必要ならばファイル選択後の処理を変更してください
      fileSelectMutation.mutateAsync(selectedFile.id)
    } else {
      toast?.show({
        severity: 'warn',
        summary: '選択なし',
        detail: 'Excelファイルを選択してください',
        life: 3000
      })
    }
  }

  // ソート処理
  const handleSort = (e: DataTableSortEvent) => {
    setSortField(e.sortField)
    setSortOrder(e.sortOrder === 1 ? 1 : -1)
  }

  const iconTemplate = (rowData: OneDriveFile) => {
    if (rowData.type === 'folder') {
      return <i className="pi pi-folder text-primary text-xl mr-2"></i>
    } else if (rowData.is_excel) {
      return <i className="pi pi-file-excel text-success text-xl mr-2"></i>
    }
    return <i className="pi pi-file text-xl mr-2"></i>
  }

  const nameTemplate = (rowData: OneDriveFile) => {
    return (
      <div className="flex align-items-center">
        {iconTemplate(rowData)}
        <span>{rowData.name}</span>
      </div>
    )
  }

  const typeTemplate = (rowData: OneDriveFile) => {
    return rowData.type === 'folder' ? 'フォルダ' : 'Excelファイル'
  }

  const header = (
    <div className="flex flex-column md:flex-row justify-content-between">
      <BreadCrumb
        model={breadcrumbItems}
        home={{ icon: 'pi pi-home', command: () => { setCurrentPath(null); setPathHistory([]); setSelectedFile(null) } }}
        className="mb-3 md:mb-0 p-0 border-none"
      />
      <div className="p-inputgroup w-full md:w-20rem">
        <span className="p-inputgroup-addon">
          <i className="pi pi-search"></i>
        </span>
        <InputText
          placeholder="ファイル名でフィルター"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
        {filterText && (
          <Button
            icon="pi pi-times"
            className="p-button-outlined"
            onClick={() => setFilterText('')}
          />
        )}
      </div>
    </div>
  )

  return (
    <Dialog
    header="OneDriveからファイルを選択"
    footer={
      <div className="flex justify-content-end mt-3">
        <Button
          label="ファイルを選択"
          icon="pi pi-arrow-right"
          disabled={!selectedFile || !selectedFile.is_excel}
          onClick={handleProceed}
        />
      </div>
    }
    visible={visible} onHide={onClose} style={{ width: '80vw' }}>
      <div className="flex flex-column align-items-center">
        <div className="w-full max-w-screen-lg">
          <div className="mb-3">
            <p>
              処理したいExcelファイルをOneDriveから選択してください。フォルダをクリックすると中に移動します。
              カラムヘッダーをクリックすると並べ替えができます。
            </p>
          </div>
          <div className="shadow-2">
            <DataTable
              value={filteredFiles}
              header={header}
              loading={isLoading || fileSelectMutation.isPending}
              emptyMessage="ファイルが見つかりません"
              selectionMode="single"
              selection={selectedFile}
              onSelectionChange={(e) => setSelectedFile(e.value as OneDriveFile)}
              onRowClick={handleRowClick}
              rowClassName={(data) =>
                data.type === 'folder' ? 'cursor-pointer' : data.is_excel ? 'cursor-pointer' : ''
              }
              className="p-datatable-sm"
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={handleSort}
              removableSort
            >
              <Column field="name" header="名前" body={nameTemplate} sortable className="w-[16rem]" />
              <Column field="fileType" header="種類" body={typeTemplate} sortable sortField="type"  />
              <Column field="size" header="サイズ" sortable />
              <Column
                field="last_modified"
                header="更新日時"
                sortable
                body={(rowData: OneDriveFile) => formatDateTime(rowData?.last_modified)}
              />
            </DataTable>

          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default OneDriveFileSelectorModal
